import React, { useState, useRef } from 'react';
import { Dialog, Grid, Button, Box, Typography, Stack } from '@mui/material';
import { Document, Page, pdfjs } from 'react-pdf';
import { Pendingwarning } from 'component/BuyersMarketplace/MakeOffers';
import { API_URL, SIGN_TEMPLATE } from 'constant/app.config';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import Loading from 'assets/images/loading.png';
import { postApiCall } from 'api/apiService';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import 'pdfjs-dist/webpack.mjs';

const PAGE_WIDTH = 800;

const FIELD_DIMENSIONS = {
  sign: { width_percent: 0.2, height_percent: 0.05 },
  date: { width_percent: 0.2, height_percent: 0.05 },
};

function PrepareContractSignDialog({
  open,
  onCancel,
  onSuccess,
  pdfFile,
  streetName,
  matchessm,
  contractFileUrl,
  buyerId,
  vendorId,
  VendorContainer,
  fromDashboard,
  propertyId,
  userName,
  signBuyerId,
  propertyOwnerDetails,
}) {
  const params = useParams();
  const [numPages, setNumPages] = useState(0);
  const [fields, setFields] = useState([]);
  const [pageHeights, setPageHeights] = useState({});
  const [pageDimensions, setPageDimensions] = useState({});
  const overlayRefs = useRef({});
  const isSendEnabled = (() => {
    if (fields.length === 0) return false;

    let allSignUsers = [];

    if (VendorContainer) {
      if (propertyOwnerDetails?.length > 0) {
        allSignUsers = propertyOwnerDetails.map((owner) => owner?.user?.userId);
      } else {
        return false;
      }
    } else {
      // For non-VendorContainer - single buyer scenario
      const userId = signBuyerId || buyerId;
      if (userId && userId !== 0) {
        allSignUsers = [userId];
      } else {
        return false;
      }
    }

    const allUsersHaveBothFields = allSignUsers.every((userId) => {
      if (!userId || userId === 0) return false;

      const hasSignature = fields.some(
        (f) => Number(f.signUserId) === Number(userId) && f.type === 'signature'
      );
      const hasDate = fields.some(
        (f) => Number(f.signUserId) === Number(userId) && f.type === 'date'
      );
      return hasSignature && hasDate;
    });

    return allUsersHaveBothFields;
  })();
  const scrollRef = useRef(null);
  const pageScales = useRef({});
  const AUTO_SCROLL_MARGIN = 60;
  const AUTO_SCROLL_SPEED = 18;
  const lastScrollTop = useRef(0);
  const dragOffset = useRef({ x: 0, y: 0 });
  const isMultipleOwners = propertyOwnerDetails?.length > 1;

  const PdfLoader = () => (
    <Box
      sx={{
        height: '70vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <img
        src={Loading}
        alt="Loading PDF"
        className="loaderImage"
        style={{ width: 60, height: 60 }}
      />
    </Box>
  );

  const handlePageLoadSuccess = (page, pdfPage) => {
    const viewport = pdfPage.getViewport({ scale: 1 });
    const scale = PAGE_WIDTH / viewport.width;

    setPageDimensions((prev) => ({
      ...prev,
      [page]: {
        width: viewport.width,
        height: viewport.height,
        scale: scale,
      },
    }));

    setPageHeights((prev) => ({
      ...prev,
      [page]: viewport.height * scale,
    }));

    pageScales.current[page] = scale;
  };

  const handleDrop = (e, signUserId, fieldType, ownerIndex) => {
    e.preventDefault();

    const overlays = overlayRefs.current;

    let targetPage = null;
    let targetRect = null;

    Object.entries(overlays).forEach(([page, el]) => {
      const rect = el.getBoundingClientRect();

      if (
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom
      ) {
        targetPage = Number(page);
        targetRect = rect;
      }
    });

    if (!targetPage || !targetRect) return;

    const pageDim = pageDimensions[targetPage];
    if (!pageDim) return;

    const overlayEl = overlayRefs.current[targetPage];

    const canvas = overlayEl.parentElement.querySelector('canvas');
    const canvasRect = canvas.getBoundingClientRect();

    const mouseX = e.clientX - canvasRect.left;
    const mouseY = e.clientY - canvasRect.top;

    const adjustedX = mouseX - dragOffset.current.x;
    const adjustedY = mouseY - dragOffset.current.y;

    let x_percent = adjustedX / canvasRect.width;
    let y_percent = adjustedY / canvasRect.height;

    const clampedXPercent = Math.max(
      0,
      Math.min(1 - FIELD_DIMENSIONS[fieldType].width_percent, x_percent)
    );

    const clampedYPercent = Math.max(
      0,
      Math.min(1 - FIELD_DIMENSIONS[fieldType].height_percent, y_percent)
    );

    const fieldId = `${fieldType}_${ownerIndex}`;

    setFields((prev) => [
      ...prev,
      {
        fieldId,
        signUserId,
        type: fieldType === 'sign' ? 'signature' : 'date',
        page: targetPage,
        x_percent: clampedXPercent,
        y_percent: clampedYPercent,
        ...FIELD_DIMENSIONS[fieldType],
      },
    ]);
  };

  const removeField = (fieldId) => {
    setFields((prev) => prev.filter((item) => item.fieldId !== fieldId));
  };

  const Stamp = ({ fieldKey, field }) => {
    const dragOffset = useRef({ x: 0, y: 0 });
    const pageDim = pageDimensions[field.page];
    if (!pageDim) return null;

    const scale = pageScales.current[field.page] || 1;

    const pixelX = field.x_percent * PAGE_WIDTH;
    const pixelY =
      field.y_percent * overlayRefs.current[field.page]?.offsetHeight;
    const pixelWidth = field.width_percent * PAGE_WIDTH;
    const pixelHeight =
      field.height_percent * overlayRefs.current[field.page]?.offsetHeight;

    const onMouseDown = (e) => {
      if (e.target.closest('.delete-icon')) return;
      lastScrollTop.current = scrollRef.current?.scrollTop || 0;

      e.preventDefault();

      const overlayEl = overlayRefs.current[field.page];
      if (!overlayEl) return;

      const overlayRect = overlayEl.getBoundingClientRect();
      const scale = pageScales.current[field.page] || 1;

      const mouseX = e.clientX - overlayRect.left;
      const mouseY = e.clientY - overlayRect.top;

      const fieldX = field.x_percent * PAGE_WIDTH;
      const fieldY =
        field.y_percent * overlayRefs.current[field.page]?.offsetHeight;
      dragOffset.current = {
        x: mouseX - fieldX,
        y: mouseY - fieldY,
      };

      const onMove = (me) => {
        const scrollEl = scrollRef.current;
        if (scrollEl) {
          const scrollRect = scrollEl.getBoundingClientRect();

          if (me.clientY < scrollRect.top + AUTO_SCROLL_MARGIN) {
            scrollEl.scrollTop -= AUTO_SCROLL_SPEED;
          } else if (me.clientY > scrollRect.bottom - AUTO_SCROLL_MARGIN) {
            scrollEl.scrollTop += AUTO_SCROLL_SPEED;
          }
        }

        const targetEl = overlayRefs.current[field.page];
        if (!targetEl) return;

        const rect = targetEl.getBoundingClientRect();
        const scale = pageScales.current[field.page] || 1;

        const newPixelX = me.clientX - rect.left - dragOffset.current.x;
        const newPixelY = me.clientY - rect.top - dragOffset.current.y;

        const newXPercent = newPixelX / PAGE_WIDTH;
        const newYPercent =
          newPixelY / overlayRefs.current[field.page]?.offsetHeight;

        const clampedXPercent = Math.max(
          0,
          Math.min(1 - field.width_percent, newXPercent)
        );

        const clampedYPercent = Math.max(
          0,
          Math.min(1 - field.height_percent, newYPercent)
        );
        setFields((prev) =>
          prev.map((item) =>
            item.fieldId === fieldKey
              ? {
                  ...item,
                  x_percent: clampedXPercent,
                  y_percent: clampedYPercent,
                }
              : item
          )
        );
      };

      const onUp = (me) => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);

        const overlays = overlayRefs.current;
        let targetPage = field.page;
        let targetRect = null;

        Object.entries(overlays).forEach(([page, el]) => {
          const rect = el.getBoundingClientRect();
          if (
            me.clientX >= rect.left &&
            me.clientX <= rect.right &&
            me.clientY >= rect.top &&
            me.clientY <= rect.bottom
          ) {
            targetPage = Number(page);
            targetRect = rect;
          }
        });

        if (!targetRect) return;

        const targetPageDim = pageDimensions[targetPage];
        if (!targetPageDim) return;

        const scale = pageScales.current[targetPage] || 1;

        const finalPixelX = me.clientX - targetRect.left - dragOffset.current.x;
        const finalPixelY = me.clientY - targetRect.top - dragOffset.current.y;

        const finalXPercent = finalPixelX / PAGE_WIDTH;
        const finalYPercent =
          finalPixelY / overlayRefs.current[targetPage]?.offsetHeight;

        const clampedXPercent = Math.max(
          0,
          Math.min(1 - field.width_percent, finalXPercent)
        );

        const clampedYPercent = Math.max(
          0,
          Math.min(1 - field.height_percent, finalYPercent)
        );
        setFields((prev) =>
          prev.map((item) =>
            item.fieldId === fieldKey
              ? {
                  ...item,
                  page: targetPage,
                  x_percent: clampedXPercent,
                  y_percent: clampedYPercent,
                }
              : item
          )
        );
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    };

    return (
      <Box
        onMouseDown={onMouseDown}
        sx={{
          position: 'absolute',
          top: pixelY,
          left: pixelX,
          width: pixelWidth,
          height: pixelHeight,
          background: '#6BBBE8',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'move',
          zIndex: 5,
        }}
      >
        <Typography
          sx={{ fontFamily: 'Poppins', fontWeight: 600, color: '#fff' }}
        >
          {field.type === 'signature' ? 'Signature' : 'Date'}
        </Typography>
        <Box
          className="delete-icon"
          onClick={(e) => {
            e.stopPropagation();
            removeField(fieldKey);
          }}
          sx={{
            position: 'absolute',
            right: '-28px',
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: '#fff',
            borderRadius: '50%',
            border: '1px solid #E0E0E0',
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
            '&:hover': {
              backgroundColor: '#FDECEA',
            },
          }}
        >
          <DeleteOutlineOutlinedIcon sx={{ fontSize: '18px' }} />
        </Box>
      </Box>
    );
  };

  const [loader, setLoader] = useState(false);

  const handleSend = async () => {
    setLoader(true);
    try {
      const Y_CORRECTION_FACTOR = 1.0;

      const payload = {
        propertyId: fromDashboard ? propertyId : params?.dashboardId,
        buyerId: VendorContainer ? 0 : buyerId,
        vendorId: VendorContainer ? vendorId : 0,
        contractFile: contractFileUrl,
        signBuyerId: signBuyerId ? signBuyerId : 0,
        fields: Object.values(fields).map((f) => ({
          fieldId: f.fieldId,
          type: f.type,
          page: f.page,
          x_percent: parseFloat(f.x_percent.toFixed(4)),
          y_percent: parseFloat((f.y_percent * Y_CORRECTION_FACTOR).toFixed(4)),
          width_percent: f.width_percent,
          height_percent: f.height_percent,
          signUserId: Number(f.signUserId) || Number(buyerId),
        })),
      };
      const response = await postApiCall(SIGN_TEMPLATE, payload);

      const { status, heading, message } = response?.data || {};
      setLoader(false);

      if (status === 1) {
        const selectedUsers = propertyOwnerDetails?.filter((owner) =>
          fields.some(
            (f) => Number(f.signUserId) === Number(owner?.user?.userId)
          )
        );

        const userType = VendorContainer ? 'Vendor' : 'Buyer';
        let displayText = '';

        if (selectedUsers?.length > 1) {
          const userTypeText = `${userType}s`;
          const staticText = `The E-Sign request has been successfully sent to the ${userTypeText}.`;

          let userListHtml = selectedUsers
            .map(
              (owner) => `
      <div style="margin-top:4px;">
        - <span style="font-weight:600;">${owner?.user?.userName || ''} ${
                owner?.user?.userSurName || ''
              }</span>
      </div>
    `
            )
            .join('');

          displayText = `
  <div style="margin-bottom:12px;">
    ${staticText}
  </div>
  <div style="text-align:left; padding-left:20px;">
    ${userListHtml}
  </div>
    `;
        } else if (selectedUsers?.length === 1) {
          const userName = `${selectedUsers[0]?.user?.userName || ''} ${
            selectedUsers[0]?.user?.userSurName || ''
          }`.trim();
          displayText = `The E-Sign request has been successfully sent to the ${userType}, <span style="font-weight:600;">${userName}</span>.`;
        } else {
          let formattedMessage = message?.replace(/e-sign/gi, 'E-Sign') || '';

          const match = formattedMessage.match(
            new RegExp(`${userType},\\s*(.+)\\.`)
          );
          if (match && match[1]) {
            const name = match[1];
            formattedMessage = formattedMessage.replace(
              name,
              `<span style="font-weight:600;">${name}</span>`
            );
          }

          displayText = formattedMessage;
        }

        await Swal.fire({
          icon: 'success',
          title: heading,
          html: displayText,
          confirmButtonText: 'Close',
        });

        onSuccess();
      } else {
        await Swal.fire({
          icon: 'error',
          title: heading,
          text: message,
          confirmButtonText: 'Close',
        });
      }
    } catch (error) {
      setLoader(false);

      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text:
          error?.response?.data?.message ||
          'Unable to complete the request. Please try again.',
        confirmButtonText: 'Close',
      });
    }
  };
  const FieldItem = ({
    label,
    draggable,
    fieldType,
    signUserId,
    disabled,
    ownerIndex,
  }) => (
    <Box
      draggable={draggable && !disabled}
      onDragStart={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();

        dragOffset.current = {
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        };
        e.dataTransfer.setData('ownerIndex', ownerIndex);
        e.dataTransfer.setData('fieldType', fieldType);
        e.dataTransfer.setData('signUserId', signUserId);
      }}
      sx={{
        display: 'grid',
        gridTemplateColumns: '24px 1fr 24px',
        alignItems: 'center',
        backgroundColor: '#4CA4D6',
        color: '#FFFFFF',
        borderRadius: '4px',
        padding: '10px 12px',
        mb: 1.5,
        cursor: draggable && !disabled ? 'grabbing' : 'default',
        opacity: disabled ? 0.5 : 1,
        userSelect: 'none',
      }}
    >
      <Box sx={{ textAlign: 'center', opacity: 0.9 }}>⋮⋮</Box>

      <Typography
        sx={{
          fontFamily: 'Poppins',
          fontSize: '14px',
          fontWeight: 500,
          textAlign: 'center',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>

      <Box sx={{ textAlign: 'center', opacity: 0.9 }}>⋮⋮</Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      maxWidth={matchessm ? false : 'xl'}
      fullWidth
      fullScreen={matchessm}
      PaperProps={{
        sx: {
          borderRadius: '5px',
          minWidth: matchessm ? '95%' : '420px',
          padding: matchessm ? '30px 20px' : '30px',
          boxShadow: '-4px 4px rgba(0, 0, 0, 0.09)',
        },
      }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography
            sx={{
              fontFamily: 'Poppins',
              fontSize: '14px',
              color: '#666666',
            }}
          >
            {streetName}
          </Typography>
        </Grid>

        <Grid
          item
          xs={12}
          display="flex"
          alignItems="center"
          padding="12px 0 20px"
        >
          <Pendingwarning />

          <Typography
            sx={{
              fontFamily: 'Poppins',
              fontWeight: 500,
              fontSize: '24px',
              paddingLeft: '12px',
            }}
          >
            Prepare Contract for Signature
            {!isMultipleOwners ? ` - ${userName}` : ''}
          </Typography>
        </Grid>

        <Grid item xs={3}>
          <Stack
            sx={{
              border: '1px solid #E0E0E0',
              borderRadius: '4px',
              padding: '16px',
              gap: 1,
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 500,
                fontSize: '14px',
                mb: 2,
                marginBottom: 1,
              }}
            >
              Drag & Drop to Assign Fields
            </Typography>
            <Box>
              {propertyOwnerDetails?.length > 0 && VendorContainer
                ? propertyOwnerDetails.map((owner, index) => {
                    const fullName = `${owner?.user?.userName || ''} ${
                      owner?.user?.userSurName || ''
                    }`;

                    const signDisabled = fields.some(
                      (f) =>
                        f.signUserId == owner?.user?.userId &&
                        f.type === 'signature'
                    );

                    const dateDisabled = fields.some(
                      (f) =>
                        f.signUserId == owner?.user?.userId && f.type === 'date'
                    );

                    return (
                      <Box key={owner?.user?.userId} sx={{ mb: 3 }}>
                        {isMultipleOwners && (
                          <Typography
                            sx={{
                              fontFamily: 'Poppins',
                              fontWeight: 600,
                              fontSize: '14px',
                              mb: 1,
                            }}
                          >
                            {fullName}
                          </Typography>
                        )}

                        <FieldItem
                          label="Signature"
                          draggable
                          fieldType="sign"
                          signUserId={owner?.user?.userId}
                          ownerIndex={index + 1}
                          disabled={signDisabled}
                        />

                        <FieldItem
                          label="Date"
                          draggable
                          fieldType="date"
                          signUserId={owner?.user?.userId}
                          ownerIndex={index + 1}
                          disabled={dateDisabled}
                        />
                      </Box>
                    );
                  })
                : (() => {
                    const userId = signBuyerId || buyerId;
                    const fullName = userName || 'Buyer';

                    const signDisabled = fields.some(
                      (f) => f.signUserId == userId && f.type === 'signature'
                    );

                    const dateDisabled = fields.some(
                      (f) => f.signUserId == userId && f.type === 'date'
                    );

                    return (
                      <Box key={userId || 'buyer'} sx={{ mb: 3 }}>
                        <Typography
                          sx={{
                            fontFamily: 'Poppins',
                            fontWeight: 600,
                            fontSize: '14px',
                            mb: 1,
                          }}
                        >
                          {fullName}
                        </Typography>

                        <FieldItem
                          label="Signature"
                          draggable
                          fieldType="sign"
                          signUserId={userId}
                          ownerIndex={1}
                          disabled={signDisabled}
                        />

                        <FieldItem
                          label="Date"
                          draggable
                          fieldType="date"
                          signUserId={userId}
                          ownerIndex={1}
                          disabled={dateDisabled}
                        />
                      </Box>
                    );
                  })()}
            </Box>
          </Stack>
        </Grid>

        <Grid item xs={9}>
          <Box
            sx={{
              border: '1px solid #DADADA',
              borderRadius: '6px',
              padding: '16px',
              height: '100%',
              backgroundColor: '#FFFFFF',
            }}
          >
            <Box
              ref={scrollRef}
              sx={{
                height: '70vh',
                overflow: 'auto',
                backgroundColor: '#F5F5F5',
                borderRadius: '4px',
              }}
            >
              <Box
                sx={{
                  padding: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Document
                  file={pdfFile}
                  loading={<PdfLoader />}
                  noData={null}
                  onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                >
                  {Array.from({ length: numPages }, (_, i) => {
                    const page = i + 1;

                    return (
                      <Box key={page} sx={{ position: 'relative', mb: 3 }}>
                        <Page
                          pageNumber={page}
                          width={PAGE_WIDTH}
                          loading={null}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                          onLoadSuccess={(pdfPage) =>
                            handlePageLoadSuccess(page, pdfPage)
                          }
                        />
                        <Box
                          ref={(el) => (overlayRefs.current[page] = el)}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: PAGE_WIDTH,
                            height: pageHeights[page],
                            zIndex: 1,
                            pointerEvents: 'all',
                          }}
                          onDrop={(e) => {
                            const fieldType =
                              e.dataTransfer.getData('fieldType');

                            const signUserId =
                              e.dataTransfer.getData('signUserId');

                            const ownerIndex =
                              e.dataTransfer.getData('ownerIndex');

                            handleDrop(e, signUserId, fieldType, ownerIndex);
                          }}
                          onDragOver={(e) => e.preventDefault()}
                        >
                          {fields
                            .filter((f) => f.page === page)
                            .map((f) => (
                              <Stamp
                                key={f.fieldId}
                                fieldKey={f.fieldId}
                                field={f}
                              />
                            ))}
                        </Box>
                      </Box>
                    );
                  })}
                </Document>
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={3} />

        <Grid item xs={9}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              mt: 3,
            }}
          >
            <Button
              size="medium"
              variant="outlined"
              onClick={onCancel}
              sx={{
                padding: matchessm ? '6px 18px' : '10px 39px',
                height: matchessm ? '35px' : '41px',
                fontSize: matchessm ? '12px' : '14px',
                color: '#666666',
                fontFamily: 'Poppins',
                border: '1px solid rgba(17, 35, 71, 0.1)',
                borderRadius: '5px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(17, 35, 71, 0.1)',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              size="medium"
              variant="contained"
              onClick={handleSend}
              disabled={!isSendEnabled || loader}
              sx={{
                padding: matchessm ? '7px 17px' : '10px 39px',
                height: matchessm ? '35px' : '41px',
                fontSize: matchessm ? '12px' : '14px',
                color: '#FFFFFF',
                fontFamily: 'Poppins',
                background: '#4CA4D6',
                borderRadius: '5px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#4CA4D6',
                },
                '&:disabled': {
                  background: '#BFDDF0',
                  color: '#FFFFFF',
                },
              }}
            >
              Send
            </Button>
            {loader && (
              <img
                src={Loading}
                alt="Loader"
                className="loaderImage"
                style={{
                  height: '32px',
                  width: '32px',
                  marginLeft: '4px',
                }}
              />
            )}
          </Box>
        </Grid>
      </Grid>
    </Dialog>
  );
}

export default PrepareContractSignDialog;
